<template>
  <div class="operations">
    <div class="nav_brand_block">
      <div class="brand_badge">
        <div class="brand_logo logo_white"></div>
      </div>
    </div>

    <div class="operations_list">
      <template v-for="operation in operations" :key="operation.type">
        <button v-if="operation.isShow" class="operation_item" :class="{ selected: operate.getActionType().value == operation.type }" @click="actionType(operation.type)">
          <div class="item_highlight"></div>

          <div class="operation_main">
            <div class="icon_wrapper" :class="operation.icon_class"></div>
            <div class="item_text_group">
              <div class="item_text">{{ operation.title }}</div>
              <div class="item_sub_text">{{ operation.decor }}</div>
            </div>
          </div>

          <div class="active_dot"></div>
        </button>
      </template>
    </div>

    <div class="oper_bottom_container">
      <div class="pair_btn" v-if="isShowPairButton" @click="pairDevice">
        <span class="pair_icon"></span>
        <span>{{ $t('pair24g') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { operate, OperateType } from '../../service/driver/operate'
import { onMounted, ref, watch, computed } from 'vue'
import { DeviceManager } from '@/service/device'
import { configSet } from '@/service/driver/config_set'
import { calibrationHandler } from '@/service/driver/calibration_set'
import { isShow42GPair, isShowPairButton } from '../pair_24g/pair_24g.vue'
import i18n from '@/languages/i18n'
import { isShowLeftConfigMgx, mouseOperationsSort } from '../../define/showConfig'
import { specialCheck, speciaBehiver } from '@/define/limit'
import { getCurrentDevice } from '@/profiles/selector'
import { MouseDevices } from '@/define/device_vendor'
const { t } = i18n.global

const actionType = (type: OperateType) => {
  configSet.isShowShortcon = false
  operate.changeActionType(type)
}

const isShowLight = ref(false)

watch(
  () => calibrationHandler.state.initCalibrationStatus,
  (value: boolean) => {
    if (value == false) {
      return
    }
    if (isShowLeftConfigMgx.value) {
      operate.changeActionType(OperateType.mgx)
    }
  },
)

const initLight = async () => {
  const list = await getCurrentDevice.value.getLightEffectList()
  isShowLight.value = Object.keys(list).length > 0
}

onMounted(async () => {
  await initLight()
  if (isShowLeftConfigMgx.value) {
    operate.changeActionType(OperateType.mgx)
  } else if (MouseDevices.includes(DeviceManager.instance.state.currentPid)) {
    operate.changeActionType(OperateType.base)
  } else if (isShowLight.value) {
    operate.changeActionType(OperateType.light)
  } else {
    operate.changeActionType(OperateType.action)
  }
})

interface OperationItem {
  title: string
  decor: string
  icon_class: string
  type: OperateType
  isShow: boolean
}

const operations = computed(() => {
  const list: OperationItem[] = [
    {
      title: t('magnet_set_title'),
      decor: 'TRIGGER',
      icon_class: 'mgx',
      type: OperateType.mgx,
      isShow: isShowLeftConfigMgx.value,
    },
    {
      title: t('light_effect_title'),
      decor: 'LIGHTING',
      icon_class: 'light',
      type: OperateType.light,
      isShow: isShowLight.value,
    },
    {
      title: t('action_set_title'),
      decor: 'MAPPING',
      icon_class: 'action',
      type: OperateType.action,
      isShow: true,
    },
    {
      title: t('advanced_settings'),
      decor: 'ADVANCED',
      icon_class: 'advanced',
      type: OperateType.advanced,
      isShow: specialCheck(speciaBehiver.SupportAdvancedSetting, DeviceManager.instance.state.currentPid),
    },
    {
      title: t('base_set_title'),
      decor: 'SYSTEM',
      icon_class: 'base',
      type: OperateType.base,
      isShow: true,
    },
  ]

  if (MouseDevices.includes(DeviceManager.instance.state.currentPid)) {
    list.sort((a, b) => {
      return mouseOperationsSort.indexOf(a.type) - mouseOperationsSort.indexOf(b.type)
    })
  }

  return list
})

const pairDevice = () => {
  isShow42GPair.value = true
}
</script>

<style lang="less" scoped>
.operations {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 0;
  padding: 18px 8px 16px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  /* 强制裁剪合成层边缘，消除 Chromium backdrop-filter 亚像素渲染白色伪影 */
  -webkit-mask-image: linear-gradient(#000, #000);
  mask-image: linear-gradient(#000, #000);

  .nav_brand_block {
    display: flex;
    justify-content: center;
    padding: 4px 0 20px;

    .brand_badge {
      width: 64px;
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
    }

    .brand_logo {
      width: 64px;
      height: 64px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      position: relative;
      z-index: 1;
      filter: drop-shadow(0 6px 16px rgba(var(--color-brand-1-rgb), 0.22));
      transform: translateY(-1px);
    }
  }

  .operations_list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    border-top: none;
  }

  .operation_item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 128px;
    padding: 16px 6px 14px;
    cursor: pointer;
    border: none;
    border-radius: 22px;
    background: transparent;
    transition:
      background-color 0.4s,
      transform 0.4s,
      box-shadow 0.4s,
      border-color 0.4s;
    color: var(--sidebar-text);
    position: relative;
    overflow: visible;
    appearance: none;
    text-align: center;

    .item_highlight {
      position: absolute;
      inset: 4px 6px;
      border-radius: 18px;
      border: 1px solid transparent;
      background: rgba(255, 255, 255, 0);
      opacity: 0;
      transition: all 0.4s var(--transition-base);
    }

    .operation_main {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      min-width: 0;
      width: 100%;
    }

    .icon_wrapper {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: transparent;
      position: relative;
      flex-shrink: 0;
      color: var(--sidebar-text-muted);
      transform: rotate(-3deg);
      transition: all 0.4s var(--transition-base);

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background-color: currentColor;
        mask-size: 16px 16px;
        mask-repeat: no-repeat;
        mask-position: center;
      }

      &.mgx::after {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M13 2L3 14h9l-1 8 10-12h-9l1-8z'/%3E%3C/svg%3E");
      }

      &.light::after {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A5 5 0 0 0 8 8c0 1.3.5 2.6 1.5 3.5.8.8 1.3 1.5 1.5 2.5'/%3E%3Cpath d='M9 18h6'/%3E%3Cpath d='M10 22h4'/%3E%3C/svg%3E");
      }

      &.action::after {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='12' height='12' rx='1.5'/%3E%3Crect x='9' y='9' width='12' height='12' rx='1.5'/%3E%3C/svg%3E");
      }

      &.base::after {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='7' height='7'/%3E%3Crect x='14' y='3' width='7' height='7'/%3E%3Crect x='14' y='14' width='7' height='7'/%3E%3Crect x='3' y='14' width='7' height='7'/%3E%3C/svg%3E");
      }

      &.advanced::after {
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z'/%3E%3Ccircle cx='12' cy='12' r='3'/%3E%3C/svg%3E");
      }
    }

    .item_text_group {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      min-width: 0;
    }

    .item_text {
      min-width: 0;
      font-size: 11px;
      font-weight: 900;
      letter-spacing: 0.08em;
      line-height: 1.2;
      text-transform: uppercase;
      color: var(--sidebar-text-muted);
      transition: color 0.4s var(--transition-base);
    }

    .item_sub_text {
      min-width: 0;
      font-size: 8px;
      font-weight: 800;
      letter-spacing: 0.24em;
      color: var(--sidebar-text-muted);
      line-height: 1;
      transition: all 0.4s var(--transition-base);
    }

    .active_dot {
      position: absolute;
      bottom: 8px;
      left: 50%;
      width: 10px;
      height: 2px;
      border-radius: var(--radius-pill);
      background: var(--brand-color-pure);
      box-shadow: 0 0 8px rgba(var(--color-brand-1-rgb), 0.7);
      transform: translateX(-50%) scaleX(0);
      opacity: 0;
      transition: all 0.4s var(--transition-base);
    }

    &:hover:not(.selected) {
      .item_highlight {
        background: rgba(255, 255, 255, 0.45);
        border-color: rgba(255, 255, 255, 0.5);
        opacity: 1;
      }

      transform: translateY(-2px);
      box-shadow: none;

      .icon_wrapper {
        background: rgba(255, 255, 255, 0.9);
        color: var(--color-dark);
        box-shadow: 0 8px 18px rgba(0, 0, 0, 0.05);
        transform: rotate(0deg);
      }

      .item_text {
        color: var(--color-dark);
      }
    }

    &.selected {
      color: var(--brand-color-pure);
      box-shadow: none;
      transform: translateY(-2px);

      .item_highlight {
        background: rgba(255, 255, 255, 0.9);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
        opacity: 1;
      }

      .icon_wrapper {
        background: var(--brand-color);
        color: #ffffff;
        box-shadow: 0 10px 24px rgba(var(--color-brand-2-rgb), 0.24);
        transform: rotate(0deg);
      }

      .item_text {
        color: var(--color-dark);
      }

      .item_sub_text {
        color: var(--brand-color-pure);
      }

      .active_dot {
        opacity: 1;
        transform: translateX(-50%) scaleX(1);
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: none;
      }
    }
  }

  .oper_bottom_container {
    margin-top: auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 14px;

    .pair_btn {
      width: 100%;
      min-height: 44px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      background: rgba(255, 255, 255, 0.08);
      border: var(--panel-line-soft);
      border-radius: 12px;
      color: var(--sidebar-text);
      font-weight: 700;
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
      transition:
        background-color 0.4s,
        transform 0.4s,
        border-color 0.4s;

      .pair_icon {
        width: 14px;
        height: 14px;
        background-color: currentColor;
        mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M16 18l6-6-6-6'/%3E%3Cpath d='M8 6l-6 6 6 6'/%3E%3Cpath d='M14.5 7.5l-5 9'/%3E%3C/svg%3E");
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.48);
        border-color: rgba(255, 255, 255, 0.34);
        transform: translateY(-2px);
      }
    }
  }
}
</style>
